import { memo, useEffect, useState } from 'react';
import { Link, useLocation } from 'wouter';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from '@infra/i18n';
import { ChevronRight, ChevronDown, Globe, LogOut, User as UserIcon } from 'lucide-react';
import { cn } from '@shared/lib';
import { BrandLogo } from '@shared/ui/brand';
import { Tooltip, TooltipContent, TooltipTrigger } from '@shared/ui';
import {
  APP_SIDEBAR_WIDTH,
  APP_SIDEBAR_EASE,
  APP_SIDEBAR_DURATION,
  brandColors,
} from '@shared/constants';
import { logout } from '@services/storage';
import { LanguageOptions } from '@shared/ui/layout';
import { useLanguagePickerVisible } from '@shared/ui/layout';
import { menuByRole, navItemIsActive, type NavItem } from './menuByRole';
import { portalForAccessRole, roleCan, type AccessRole } from '@services/access';
import { accountPaths } from '../model/account-paths';

/** Stable test id from the item key, kebab-cased: coachingHub -> nav-coaching-hub. */
function navTestId(item: NavItem): string {
  if (item.testId) return item.testId;
  const key = item.i18nKey ?? item.label;
  return `nav-${key
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/[\s]+/g, '-')
    .toLowerCase()}`;
}

// ─── Nav Link ─────────────────────────────────────────────────────────────────
interface NavLinkProps {
  item: NavItem;
  collapsed: boolean;
  location: string;
}

function NavLink({ item, collapsed, location }: NavLinkProps) {
  const { t } = useTranslation();
  const isActive = navItemIsActive(item, location);
  const displayLabel = item.i18nKey ? t(`common:nav.${item.i18nKey}`) : item.label;
  const displayBadge = item.badge === 'New' ? t('common:nav.new') : item.badge;

  const content = (
    <Link href={item.path}>
      <motion.div
        className={cn(
          'relative flex cursor-pointer items-center overflow-hidden rounded-xl border border-transparent',
          // 44px floor is a touch rule, so it applies on phones only; desktop keeps
          // the design's 42px row.
          'max-md:min-h-11',
          collapsed
            ? 'w-full justify-center gap-0 px-0 py-3'
            : 'w-full gap-[13px] px-[14px] py-[11px]',
          isActive ? 'nav-active' : 'hover-elevate text-landing-ink-secondary'
        )}
        whileTap={{ scale: 0.97 }}
        data-testid={navTestId(item)}
      >
        <item.icon
          className={cn(
            'h-5 w-5 flex-shrink-0 transition-all duration-200',
            isActive ? 'text-tone-on-accent' : 'text-landing-ink-nav'
          )}
        />

        <AnimatePresence>
          {!collapsed && (
            // Opacity only. Animating `width: 0 → auto` here ran a layout pass per
            // frame for every nav item at once, on top of the aside's own width
            // tween; the parent already clips with `overflow-hidden`, so fading is
            // visually equivalent and composites instead of reflowing.
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="flex min-w-0 items-center gap-2 overflow-hidden"
            >
              <span
                className={cn(
                  'truncate text-[14.5px] font-semibold tracking-[0.01em]',
                  isActive ? 'text-tone-on-accent' : 'text-landing-ink-secondary'
                )}
              >
                {displayLabel}
              </span>
              {item.badge && (
                <span
                  className={cn(
                    'flex-shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest',
                    isActive
                      ? 'border border-tone-glass/40 bg-tone-glass/20 text-tone-on-accent'
                      : `border border-tone-accent-wash-edge/40 bg-tone-accent-wash/50 text-[var(--brand-brand-primary-alt)]`
                  )}
                >
                  {displayBadge}
                </span>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </Link>
  );

  if (collapsed) {
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>{content}</TooltipTrigger>
        <TooltipContent side="right" className="flex items-center gap-2 font-medium">
          {displayLabel}
          {item.badge && (
            <span className="px-1.5 py-0.5 text-[9px] font-bold rounded-md bg-primary/10 text-primary uppercase border border-primary/20">
              {item.badge}
            </span>
          )}
        </TooltipContent>
      </Tooltip>
    );
  }
  return content;
}

// ─── Nav Group (parent with children) ─────────────────────────────────────────
interface NavGroupProps {
  item: NavItem;
  collapsed: boolean;
  location: string;
  onNavigate?: () => void;
}

function NavGroup({ item, collapsed, location, onNavigate }: NavGroupProps) {
  const { t } = useTranslation();
  const displayLabel = item.i18nKey ? t(`common:nav.${item.i18nKey}`) : item.label;
  const childActive = (item.children ?? []).some((child) => location.startsWith(child.path));
  const [open, setOpen] = useState(childActive);

  useEffect(() => {
    if (childActive) setOpen(true);
  }, [childActive]);

  if (collapsed) {
    return (
      <div className="space-y-1">
        {(item.children ?? []).map((child) => (
          <div key={child.path} onClick={onNavigate}>
            <NavLink item={child} collapsed location={location} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div>
      <motion.button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={cn(
          'relative flex h-11 w-full cursor-pointer items-center gap-3 overflow-hidden rounded-xl px-3',
          'hover-elevate text-landing-ink-secondary'
        )}
        whileTap={{ scale: 0.97 }}
        data-testid={navTestId(item)}
      >
        <item.icon className="h-[18px] w-[18px] flex-shrink-0 text-landing-ink-nav transition-all duration-200" />
        <span className="flex-1 text-left whitespace-nowrap text-[14.5px] font-medium tracking-[0.01em] text-landing-ink-secondary">
          {displayLabel}
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 flex-shrink-0 text-landing-ink-nav transition-transform duration-200',
            open ? 'rotate-180' : 'rotate-0'
          )}
        />
      </motion.button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.ul
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
            className="mt-1 space-y-1 overflow-hidden pl-3"
          >
            {(item.children ?? []).map((child) => (
              <li key={child.path} onClick={onNavigate}>
                <NavLink item={child} collapsed={false} location={location} />
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Sidebar Language Switcher (mobile only) ────────────────────────────────────
// On phones the header language pill is hidden; language selection lives here in
// the side panel instead. Same visibility guard as the header LanguageToggle.
function SidebarLanguage() {
  const { t } = useTranslation();
  // Checked here too so the "Language" heading does not sit alone above nothing.
  const showLanguages = useLanguagePickerVisible();
  if (!showLanguages) return null;

  return (
    <div className="mb-1">
      <div className="flex items-center gap-2 px-1 mb-2">
        <Globe className="h-3.5 w-3.5 text-landing-ink-nav" />
        <span className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-landing-ink-placeholder">
          {t('common:labels.language')}
        </span>
      </div>
      <LanguageOptions />
    </div>
  );
}

// ─── Sidebar Component ────────────────────────────────────────────────────────
interface SidebarProps {
  accessRole: AccessRole;
  collapsed: boolean;
  onToggle: () => void;
  isMobile?: boolean;
  mobileOpen?: boolean;
  onCloseMobile?: () => void;
}

export const Sidebar = memo(function Sidebar({
  accessRole,
  collapsed,
  onToggle,
  isMobile = false,
  mobileOpen = false,
  onCloseMobile,
}: SidebarProps) {
  const { t } = useTranslation();
  const [location, _setLocation] = useLocation();
  const menuItems = menuByRole(accessRole);
  const showProfile = roleCan(accessRole, 'profile.view');
  const profilePath = accountPaths(portalForAccessRole(accessRole)).profile;

  const isMobileView = isMobile;
  const isMobileOpen = mobileOpen;
  const sidebarWidth = collapsed ? APP_SIDEBAR_WIDTH.collapsed : APP_SIDEBAR_WIDTH.expanded;

  const handleCollapseClick = () => {
    if (isMobileView) {
      onCloseMobile?.();
      return;
    }
    onToggle();
  };

  const handleLogout = async () => {
    onCloseMobile?.();
    await logout();
  };

  const showCollapseLabel = isMobileView || !collapsed;

  return (
    <motion.aside
      initial={false}
      animate={{
        width: isMobileView ? APP_SIDEBAR_WIDTH.mobile : sidebarWidth,
        x: isMobileView ? (isMobileOpen ? 0 : -APP_SIDEBAR_WIDTH.mobile) : 0,
        paddingLeft: !isMobileView && collapsed ? 14 : 18,
        paddingRight: !isMobileView && collapsed ? 14 : 18,
      }}
      transition={{ duration: APP_SIDEBAR_DURATION, ease: APP_SIDEBAR_EASE }}
      className={cn(
        'fixed left-0 top-0 z-50 flex h-dvh max-h-dvh flex-col gap-1.5 pb-5 pt-[26px] sidebar-glass',
        isMobileView ? 'shadow-2xl' : ''
      )}
      data-testid="sidebar"
    >
      {/* ── Gradient top accent line ── */}
      <div className="sidebar-top-accent absolute inset-x-0 top-0 h-[2px]" />

      {/* ── Logo Section ── */}
      <div
        className={cn(
          'flex flex-shrink-0 items-center pb-[22px] pt-1.5',
          !collapsed || isMobileView ? 'px-2.5' : 'px-0'
        )}
      >
        <AnimatePresence mode="wait">
          {!collapsed || isMobileView ? (
            <motion.div
              key="expanded"
              initial={{ opacity: 0, x: -6 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -6 }}
              transition={{ duration: 0.18 }}
              className="min-w-0 flex-1"
            >
              <BrandLogo />
            </motion.div>
          ) : (
            <motion.div
              key="collapsed"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.18 }}
              className="mx-auto"
            >
              <BrandLogo collapsed />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ── Navigation ── */}
      <nav className="flex-1 min-h-0 space-y-0.5 overflow-y-auto overflow-x-hidden">
        {/* Menu Section */}
        <div className="mb-3">
          {!collapsed || isMobileView ? (
            <div className="mb-1.5 flex items-center px-3">
              <span className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-landing-ink-placeholder">
                {t('common:nav.menu') || 'Menu'}
              </span>
            </div>
          ) : (
            <div
              style={{
                textAlign: 'center',
                padding: '14px 0 6px',
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: brandColors.textTertiary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {t('common:nav.menu') || 'Menu'}
            </div>
          )}
        </div>

        <ul className="space-y-1">
          {menuItems.map((item) =>
            item.children?.length ? (
              <li key={item.path}>
                <NavGroup
                  item={item}
                  collapsed={!isMobileView && collapsed}
                  location={location}
                  onNavigate={() => isMobileView && onCloseMobile?.()}
                />
              </li>
            ) : (
              <li key={item.path} onClick={() => isMobileView && onCloseMobile?.()}>
                <NavLink item={item} collapsed={!isMobileView && collapsed} location={location} />
              </li>
            )
          )}
        </ul>
      </nav>

      {/* ── User Section & Controls ── */}
      <div className="mt-auto flex-shrink-0 space-y-0.5 pb-[max(0.25rem,env(safe-area-inset-bottom,0px))]">
        {/* Separator */}
        <div className="mb-3 h-px bg-gradient-to-r from-transparent via-tone-accent-divider/25 to-transparent" />

        {/* Language switcher — mobile only (header pill is hidden on phones) */}
        {isMobileView && <SidebarLanguage />}

        {isMobileView && (
          <>
            {showProfile && (
              <Link href={profilePath}>
                <div
                  onClick={onCloseMobile}
                  className="flex min-h-11 cursor-pointer items-center gap-3 rounded-xl px-3 text-landing-ink-secondary hover-elevate"
                  data-testid="button-view-profile"
                >
                  <UserIcon className="h-[18px] w-[18px] flex-shrink-0 text-landing-ink-nav" />
                  <span className="text-[14.5px] font-semibold tracking-[0.01em]">
                    {t('common:nav.profile')}
                  </span>
                </div>
              </Link>
            )}
            <button
              type="button"
              onClick={() => void handleLogout()}
              className="flex min-h-11 w-full items-center gap-3 rounded-xl px-3 text-tone-negative-solid transition-colors hover:bg-tone-negative-surface hover:text-tone-negative-solid-on-tint"
              data-testid="button-logout"
            >
              <LogOut className="h-[18px] w-[18px] flex-shrink-0" />
              <span className="text-[14.5px] font-semibold tracking-[0.01em]">
                {t('common:nav.signOut')}
              </span>
            </button>
          </>
        )}

        {/* Collapse */}
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <motion.button
              type="button"
              onClick={handleCollapseClick}
              className={cn(
                'flex h-9 items-center rounded-xl max-md:min-h-11',
                'text-landing-ink-placeholder transition-all duration-200 hover:bg-tone-glass/40 hover:text-landing-ink-secondary',
                !isMobileView && collapsed
                  ? 'mx-auto w-9 justify-center gap-0 px-0'
                  : 'w-full gap-3 px-3'
              )}
              whileTap={{ scale: 0.97 }}
              data-testid="button-toggle-sidebar"
            >
              <motion.div
                animate={{ rotate: !isMobileView && collapsed ? 0 : 180 }}
                transition={{ duration: 0.25 }}
              >
                <ChevronRight className="w-[16px] h-[16px]" />
              </motion.div>
              {showCollapseLabel && (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="text-[12px] font-medium"
                >
                  {t('common:nav.collapse')}
                </motion.span>
              )}
            </motion.button>
          </TooltipTrigger>
          {!isMobileView && collapsed && (
            <TooltipContent side="right">{t('common:nav.expand')}</TooltipContent>
          )}
        </Tooltip>
      </div>
    </motion.aside>
  );
});
